import styled from "styled-components";
import { Box, Flex, Text } from "rebass";
import { HiArchive } from "react-icons/hi";
import { PrimaryButton, SecondaryButton } from "../styledComponents";
import { IStorageStatus } from "../@types";

interface SummaryProps {
  clipTitle: string;
  clipThumbnail?: string;
  storageStatus: IStorageStatus;
  onSave: () => void;
  onCancel: () => void;
  isSaving: boolean;
  error?: string | null;
}

export const Summary = ({
  clipTitle,
  clipThumbnail,
  storageStatus,
  onSave,
  onCancel,
  isSaving,
  error,
}: SummaryProps) => {
  const remaining = storageStatus.limit - storageStatus.savedCount;
  const percentage = (storageStatus.savedCount / storageStatus.limit) * 100;

  return (
    <>
      <Box p="24px 28px">
        <ClipPreview mb="24px">
          {clipThumbnail ? (
            <Thumbnail src={clipThumbnail} alt={clipTitle} />
          ) : (
            <ThumbnailPlaceholder>
              <HiArchive size={24} />
            </ThumbnailPlaceholder>
          )}
          <ClipInfo>
            <ClipTitle mb={1}>{clipTitle}</ClipTitle>
          </ClipInfo>
        </ClipPreview>

        <Box>
          <StorageHeader mb="10px">
            <StorageLabel>Your Storage</StorageLabel>
            <StorageCount>
              {storageStatus.savedCount} / {storageStatus.limit} clips
            </StorageCount>
          </StorageHeader>
          <ProgressBar>
            <ProgressFill $percentage={percentage} />
          </ProgressBar>
          <StorageHint mt="10px">{remaining} slots remaining</StorageHint>
        </Box>

        {error && <ErrorText mt="20px">{error}</ErrorText>}
      </Box>

      <ModalFooter>
        <SecondaryButton onClick={onCancel} disabled={isSaving}>
          Cancel
        </SecondaryButton>
        <PrimaryButton onClick={onSave} disabled={isSaving}>
          {isSaving ? "Saving..." : "Save Clip"}
        </PrimaryButton>
      </ModalFooter>
    </>
  );
};

const ModalFooter = styled(Flex)`
  border-top: 1px solid ${({ theme }) => theme.colors.navy50};
  gap: 12px;
  justify-content: flex-end;
  padding: 16px 28px;
`;

const ClipPreview = styled(Flex)`
  align-items: center;
  background: ${({ theme }) => theme.colors.navy};
  border-radius: 12px;
  gap: 16px;
  padding: 14px;
`;

const Thumbnail = styled.img`
  border-radius: 8px;
  flex-shrink: 0;
  height: 70px;
  object-fit: cover;
  width: 124px;
`;

const ThumbnailPlaceholder = styled(Flex)`
  align-items: center;
  background: ${({ theme }) => theme.colors.navy50};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.shade400};
  flex-shrink: 0;
  height: 70px;
  justify-content: center;
  width: 124px;
`;

const ClipInfo = styled(Box)`
  flex: 1;
  min-width: 0;
`;

const ClipTitle = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-family: Barlow;
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ClipAction = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.envy};
  font-family: Barlow;
  font-size: 13px;
  font-weight: 500;
  gap: 6px;
`;

const StorageHeader = styled(Flex)`
  align-items: center;
  justify-content: space-between;
`;

const StorageLabel = styled(Text)`
  color: ${({ theme }) => theme.colors.shade200};
  font-family: Barlow;
  font-size: 14px;
  font-weight: 500;
`;

const StorageCount = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-family: Barlow;
  font-size: 14px;
  font-weight: 600;
`;

const ProgressBar = styled(Box)`
  background: ${({ theme }) => theme.colors.navy};
  border-radius: 5px;
  height: 8px;
  overflow: hidden;
  width: 100%;
`;

const ProgressFill = styled(Box)<{ $percentage: number }>`
  background: ${({ theme, $percentage }) => {
    if ($percentage >= 90) return theme.colors.alert;
    if ($percentage >= 70) return theme.colors.caution;
    return theme.colors.envy;
  }};
  border-radius: 5px;
  height: 100%;
  transition: width 0.3s ease;
  width: ${({ $percentage }) => Math.min($percentage, 100)}%;
`;

const StorageHint = styled(Text)`
  color: ${({ theme }) => theme.colors.shade400};
  font-family: Barlow;
  font-size: 13px;
`;

const ErrorText = styled(Text)`
  background: ${({ theme }) => theme.colors.alert}15;
  border: 1px solid ${({ theme }) => theme.colors.alert}40;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.alert};
  font-family: Barlow;
  font-size: 14px;
  padding: 12px 16px;
`;
